import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BsSearch, BsTrash } from "react-icons/bs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { PaginationNav1Presentation } from "./Pagination";
import Select from "react-select";
import LoadingCircle from "../utility/LoadingCircle";
import { contractTypes, stages, allowedOdooModels } from "../utility/FilterData";
import { RiErrorWarningLine } from "react-icons/ri";
import DeleteProjectModal from "./DeleteProjectModal";

const SearchProject = (props) => {
	const [projects, setProjects] = useState([]);
	const [searchParams] = useSearchParams();
	const [searchQuery, setSearchQuery] = useState("");
	const [searchContractType, setSearchContractType] = useState("");
	const [searchProjectStage, setSearchProjectStage] = useState("");
	const [searchTemplateType, setSearchTemplateType] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [start, setStart] = useState(false);
	const navigate = useNavigate();
	const currentQueryParam = searchParams.get("query") || "";
	const currentPageParam = searchParams.get("page") || "1";
	const currentContractParam = searchParams.get("contract") || "";
	const currentStageParam = searchParams.get("stage") || "";
	const currentTemplateParam = searchParams.get("template") || "";
	const currentModelParam = searchParams.get("model") || "";

	const [prevSearchQuery, setPrevSearchQuery] = useState("");

	const contractInput = useRef();
	const stageInput = useRef();
	const templateInput = useRef();
	const isClearable = useRef();

	const [odooModel, setOdooModel] = useState();
	const [initialOdooModel, setInitialOdooModel] = useState();

	const [allTemplates, setAllTemplates] = useState([]);
	const [filteredTemplates, setFilteredTemplates] = useState([]);
	const [projectCounts, setProjectCounts] = useState({});
	const [totalProjects, setTotalProjects] = useState(0);

	const [creatingProject, setCreatingProject] = useState(false);
	const [errorCreatingProject, setErrorCreatingProject] = useState(false);

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [toBeDeletedProject, setToBeDeletedProject] = useState({});
	const [deleteMessage, setDeleteMessage] = useState("Porject has successfully been deleted.");
	const [showDeleteCompleteMessage, setShowDeleteCompleteMessage] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [deleteFailed, setDeleteFailed] = useState(false);
	const defaultModel = allowedOdooModels["all"];

	const getProjectModelValue = (project) => {
		return project?.odoo_model || project?.model || currentModelParam || defaultModel.value;
	};

	const getProjectModelLabel = (project) => {
		const projectModel = getProjectModelValue(project);
		return allowedOdooModels[projectModel] ? allowedOdooModels[projectModel].label : projectModel;
	};

	const sortProjectsByModel = (projectResults) => {
		const modelSortOrder = Object.keys(allowedOdooModels);

		return [...projectResults].sort((projectA, projectB) => {
			const projectAModel = getProjectModelValue(projectA);
			const projectBModel = getProjectModelValue(projectB);
			const projectAModelIndex = modelSortOrder.indexOf(projectAModel);
			const projectBModelIndex = modelSortOrder.indexOf(projectBModel);
			const normalizedProjectAModelIndex = projectAModelIndex === -1 ? Number.MAX_SAFE_INTEGER : projectAModelIndex;
			const normalizedProjectBModelIndex = projectBModelIndex === -1 ? Number.MAX_SAFE_INTEGER : projectBModelIndex;
			const modelComparison = normalizedProjectAModelIndex - normalizedProjectBModelIndex;

			if (modelComparison !== 0) {
				return modelComparison;
			}

			const projectAName = (projectA.name || projectA.x_name || "").toString();
			const projectBName = (projectB.name || projectB.x_name || "").toString();
			return projectAName.localeCompare(projectBName);
		});
	};

	const highlightedSearchTerm = currentQueryParam && currentQueryParam !== " " ? decodeURIComponent(currentQueryParam).trim() : "";

	const escapeRegExp = (value) => {
		return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	};

	const renderHighlightedText = (value) => {
		const textValue = value === null || typeof value === "undefined" ? "" : String(value);
		if (!highlightedSearchTerm) {
			return textValue;
		}

		const safeSearchTerm = escapeRegExp(highlightedSearchTerm);
		const highlightRegex = new RegExp(`(${safeSearchTerm})`, "gi");
		const textParts = textValue.split(highlightRegex);

		if (textParts.length === 1) {
			return textValue;
		}

		return textParts.map((part, index) =>
			part.toLowerCase() === highlightedSearchTerm.toLowerCase() ? (
				<span key={`${part}-${index}`} className="bg-yellow-200 rounded ">
					{part}
				</span>
			) : (
				part
			),
		);
	};

	const renderProjectRow = (value, rowIndex, isLastRow = false) => {
		const currentProjectModel = getProjectModelValue(value);

		return (
			<div
				key={`${value.project_id || "odoo"}-${value.id}-${rowIndex}`}
				className={
					"gap-3 border-x border-b border-black p-3 flex flex-col md:flex-row md:text-center md:items-center justify-between " +
					(rowIndex % 2 === 0 ? "bg-white" : "bg-gray-200") +
					(isLastRow ? " rounded-b-lg " : "")
				}>
				{currentModelParam === "helpdesk.ticket" || currentModelParam === "all" ? (
					<div className="md:basis-1/4">
						<span className="md:hidden">{currentModelParam === "all" ? "Odoo ID: " : "Ticket #: "}</span>
						{renderHighlightedText(value.id)}
					</div>
				) : null}

				<div className="md:basis-1/4">
					{value.project_id ? (
						<Link to={"/project/" + value.project_id} className="text-blue-600 underline ">
							{renderHighlightedText(value.name || value.x_name)}
						</Link>
					) : props.isPublicStorage ? (
						<p className="text-blue-600 underline ">
							<span className="cursor-pointer hover:text-blue-800" onClick={() => createPunchwalkForPS(value)}>
								{renderHighlightedText(value.name || value.x_name)}
							</span>
						</p>
					) : (
						<Link
							to={
								"/odoo-project/" +
								value.id +
								`/template-select?model=${currentProjectModel}&contract=${
									value.x_studio_contract_type_3 ? value.x_studio_contract_type_3.toLowerCase() : "all"
								}&portfolio=${convertPortfolio(value.x_studio_portfolio_1)}`
							}
							className="text-blue-600 underline ">
							{renderHighlightedText(value.name || value.x_name)}
						</Link>
					)}
				</div>

				<p className="md:basis-1/4">
					<span className="md:hidden">Contract Type: </span>
					{value.x_studio_contract_type_3 || value.contract || value.x_studio_installation_contract_type}
				</p>
				{currentModelParam === "helpdesk.ticket" ? (
					<p className="md:basis-1/4">
						<span className="md:hidden">Status: </span>
						{value.stage_id ? value.stage_id[1] : ""}
					</p>
				) : null}
				<p className="md:basis-1/4">
					<span className="md:hidden">Project Stage: </span>
					{value.stage}
				</p>
				<p className="md:basis-1/4">
					<span className="md:hidden">Template Name: </span>
					{value.template_name}
				</p>
				<p className="md:basis-1/4">
					<span className="md:hidden">Last Updated: </span>
					{value.last_modified_on ? new Date(value.last_modified_on).toLocaleDateString("en-US") : "-"}
				</p>
				{value.project_id || value.project_id === "" ? (
					<div className="md:basis-auto flex justify-start md:justify-center items-center">
						<button
							type="button"
							className="inline-flex items-center justify-center p-1"
							onClick={() => {
								setShowDeleteCompleteMessage(false);
								setToBeDeletedProject(value);
								setShowDeleteModal(true);
							}}>
							<BsTrash />
						</button>
					</div>
				) : null}
			</div>
		);
	};

	const deleteDuplicateProject = async (value) => {
		setShowDeleteModal(true);
		setIsDeleting(true);
		setDeleteFailed(false);
		fetch(
			props.api +
				"/project/odoo/template-select/" +
				value.id +
				"?project=" +
				value.project_id +
				"&template=" +
				value.template_name +
				"&stage=" +
				value.stage,
			{
				headers: {
					Authorization: "Bearer " + props.token,
					"Content-Type": "application/json",
				},
				method: "DELETE",
				mode: "cors",
			},
		).then(async (res) => {
			let data = await res.json();
			if (!res.ok) {
				setDeleteFailed(true);
			}
			setDeleteMessage(data.message);
			setIsDeleting(false);
			setShowDeleteCompleteMessage(true);
		});
	};

	useEffect(() => {
		document.title = "Search: " + currentQueryParam + " | Page " + currentPageParam;
	}, [currentPageParam, currentQueryParam]);

	useEffect(() => {
		if (!props.api || !props.token || (allTemplates.length === 0 && props.isPublicStorage === false)) {
			return;
		}
		let queryString = "?";
		if (currentQueryParam) {
			queryString += "query=" + currentQueryParam + "&";
			let decodedQuery = decodeURIComponent(currentQueryParam);
			setSearchQuery(decodedQuery === " " ? "" : decodedQuery);
			setPrevSearchQuery(decodedQuery === " " ? "" : decodedQuery);
		}
		if (currentPageParam) {
			queryString += "page=" + currentPageParam + "&";
		}

		if (currentContractParam) {
			queryString += "contract=" + currentContractParam + "&";
			setSearchContractType(currentContractParam);
		}

		if (currentStageParam) {
			queryString += "stage=" + currentStageParam + "&";
			setSearchProjectStage(currentStageParam);
		}

		if (currentTemplateParam) {
			queryString += "template=" + currentTemplateParam + "&";
			setSearchTemplateType({ value: currentTemplateParam, label: currentTemplateParam });
		}
		if (currentModelParam) {
			queryString += "model=" + currentModelParam;
			let selectedModel = currentModelParam;
			let modelInMapping = Object.keys(allowedOdooModels).includes(selectedModel);
			setOdooModel(modelInMapping ? allowedOdooModels[currentModelParam] : defaultModel);
			setInitialOdooModel(modelInMapping ? allowedOdooModels[currentModelParam] : defaultModel);
		} else {
			setOdooModel(defaultModel);
			setInitialOdooModel(defaultModel);
			navigate(
				`/projects/search?query=${currentQueryParam}&page=${currentPageParam}&contract=${currentContractParam}&stage=${currentStageParam}&template=${currentTemplateParam}&model=${defaultModel.value}`,
				{ replace: true },
			);
			queryString += `model=${defaultModel.value}`;
		}
		setStart(true);
		setProjects([]);
		setProjectCounts({});
		setTotalProjects(0);

		const controller = new AbortController();
		const signal = controller.signal;
		setIsLoading(true);

		fetch(props.api + "/project/odoo/search" + queryString, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + props.token,
			},
			mode: "cors",
			signal,
		})
			.then((res) => {
				setIsLoading(false);
				if (res.ok) {
					return res.json();
				} else {
					setProjects([]);
					setProjectCounts({});
					setTotalProjects(0);
				}
			})
			.then((res) => {
				if (res && typeof res.projects != "undefined") {
					const processedProjects = currentModelParam === "all" ? sortProjectsByModel(res.projects) : res.projects;
					setProjects(processedProjects);
					setProjectCounts(res.counts || {});
					setTotalProjects(typeof res.total === "number" ? res.total : processedProjects.length);
				}
			})
			.catch(function (error) {
				//Handle error
				//console.log(error);
			});

		return () => {
			controller.abort();
		};
	}, [
		props.api,
		props.token,
		allTemplates,
		props.isPublicStorage,
		currentQueryParam,
		currentPageParam,
		currentContractParam,
		currentStageParam,
		currentTemplateParam,
		currentModelParam,
		navigate,
	]);

	useEffect(() => {
		if (props.isPublicStorage) {
			return;
		}
		setStart(true);
		setIsLoading(true);
		const controller = new AbortController();
		const signal = controller.signal;
		fetch(props.api + "/project/search-templates?page=1&archived=0", {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + props.token,
			},
			signal,
		})
			.then((res) => {
				if (res.ok) {
					return res.json();
				} else {
					setStart(false);
					setIsLoading(false);
				}
				// need to add error message here
			})
			.then((res) => {
				if (res) {
					setAllTemplates(
						res.templates.map((mapData) => {
							return {
								value: mapData.template.name,
								id: mapData.template.id,
								label: mapData.template.name,
								model: mapData.template.odoo_model,
							};
						}),
					);
				}
			});
		return () => {
			controller.abort();
		};
	}, [props.api, props.isPublicStorage, props.token]);

	useEffect(() => {
		if (odooModel && allTemplates) {
			setFilteredTemplates(odooModel.value === "all" ? allTemplates : allTemplates.filter((filterData) => filterData.model === odooModel.value));
		}
	}, [odooModel, allTemplates]);

	useEffect(() => {
		if (searchTemplateType && filteredTemplates && filteredTemplates.length > 0) {
			if (filteredTemplates.filter((filterData) => filterData.label === searchTemplateType.label).length === 0) {
				setSearchTemplateType("");
			}
		}
	}, [searchTemplateType, filteredTemplates]);

	useEffect(() => {
		if (showDeleteModal === false && deleteFailed === false && deleteMessage === "Porject has successfully been deleted.") {
			const deletedProjectModel = getProjectModelValue(toBeDeletedProject);
			setProjects((currentProjects) => currentProjects.filter((filterItem) => filterItem.project_id !== toBeDeletedProject.project_id));
			setProjectCounts((currentCounts) => ({
				...currentCounts,
				[deletedProjectModel]: Math.max((currentCounts[deletedProjectModel] || 0) - 1, 0),
			}));
			setTotalProjects((currentTotal) => Math.max(currentTotal - 1, 0));
			setDeleteMessage("");
		}
	}, [showDeleteModal, deleteFailed]);

	const startSearch = (e) => {
		e.preventDefault();
		let emptyProjects = [];
		if (
			prevSearchQuery !== searchQuery ||
			initialOdooModel.value !== odooModel.value ||
			searchContractType !== currentContractParam ||
			searchProjectStage !== currentStageParam ||
			searchTemplateType !== currentTemplateParam
		) {
			setProjects(emptyProjects);
		}
		const uriEncodedString = encodeURIComponent(encodeURIComponent(searchQuery));
		let newTemplateQuery = searchTemplateType ? searchTemplateType.value : "";
		if (searchQuery !== "") {
			navigate(
				`/projects/search?query=${uriEncodedString}&page=1&contract=${searchContractType}&stage=${searchProjectStage}&template=${newTemplateQuery}&model=${odooModel.value}`,
			);
		} else {
			navigate(
				`/projects/search?query=${encodeURIComponent(
					" ",
				)}&page=1&contract=${searchContractType}&stage=${searchProjectStage}&template=${newTemplateQuery}&model=${odooModel.value}`,
			);
		}
	};

	const clearAll = () => {
		contractInput.current.clearValue();
		stageInput.current.clearValue();
		templateInput.current.clearValue();
		setOdooModel(defaultModel);
	};

	const convertPortfolio = (portfolioOdooName) => {
		if (!portfolioOdooName) {
			return "all";
		}
		if (portfolioOdooName.trim().toLowerCase() === "public storage") {
			return "ps";
		} else if (portfolioOdooName.trim().toLowerCase() === "extra space storage") {
			return "ess";
		} else if (portfolioOdooName) {
			return "misc";
		} else {
			return "all";
		}
	};
	const createPunchwalkForPS = async (odooData) => {
		if (creatingProject) {
			return;
		}
		setCreatingProject(true);
		// hardcoded ps punchwalk id
		let templateId = -1;
		try {
			let response = await fetch(props.api + "/project/latest-template/" + 32, {
				method: "post",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: "Bearer " + props.token,
				},
			});
			if (response.ok) {
				let jsonData = await response.json();
				templateId = jsonData.templateId;
			}
		} catch (err) {
			setCreatingProject(false);
			setErrorCreatingProject(true);
		}

		if (templateId === -1) {
			setCreatingProject(false);
			setErrorCreatingProject(true);
			return;
		}
		try {
			let projectRes = await fetch(props.api + "/project", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: "Bearer " + props.token,
				},
				body: JSON.stringify({
					odoo_id: odooData.id,
					template_version_id: templateId,
					name: odooData.name ? odooData.name : odooData.x_name,
					model: "project.task",
				}),
			});

			if (projectRes.ok) {
				let project = await projectRes.json();
				navigate("/project/" + project.id);
			} else {
				setCreatingProject(false);
				setErrorCreatingProject(true);
			}
		} catch (err) {
			setCreatingProject(false);
			setErrorCreatingProject(true);
		}
	};

	const handlePageChange = (nextPageIndex) => {
		navigate(
			`/projects/search?query=${currentQueryParam}&page=${nextPageIndex + 1}&contract=${currentContractParam}&stage=${currentStageParam}&template=${currentTemplateParam}&model=${currentModelParam}`,
		);
	};

	const handleModelBreakdownClick = (selectedModel) => {
		navigate(
			`/projects/search?query=${currentQueryParam}&page=1&contract=${currentContractParam}&stage=${currentStageParam}&template=${currentTemplateParam}&model=${selectedModel}`,
		);
	};

	const currentSearchModel = currentModelParam;
	const isAllModelSearch = currentSearchModel === "all";
	const paginationAnchorId = "project-search-results-top";
	const parsedPageNumber = parseInt(currentPageParam, 10);
	const currentPageIndex = Number.isNaN(parsedPageNumber) || parsedPageNumber < 1 ? 0 : parsedPageNumber - 1;
	const visibleTotalProjects = totalProjects || projects.length;
	const pageCount = Math.ceil(visibleTotalProjects / 20);
	const modelBreakdownItems = Object.entries(allowedOdooModels)
		.filter(([modelKey]) => modelKey !== "all")
		.map(([modelKey, modelData]) => ({
			value: modelKey,
			label: modelData.label,
			count: projectCounts[modelKey] || 0,
		}))
		.filter((modelData) => modelData.count > 0);
	const groupedPaginatedProjects = isAllModelSearch
		? projects.reduce((groupedProjects, project, pageIndex) => {
				const projectModel = getProjectModelValue(project);
				const existingGroup = groupedProjects[groupedProjects.length - 1];

				if (!existingGroup || existingGroup.model !== projectModel) {
					groupedProjects.push({
						model: projectModel,
						label: getProjectModelLabel(project),
						items: [{ project, pageIndex }],
					});
				} else {
					existingGroup.items.push({ project, pageIndex });
				}

				return groupedProjects;
			}, [])
		: [];
	const visibleStartIndex = projects.length > 0 ? currentPageIndex * 20 + 1 : 0;
	const visibleEndIndex = projects.length > 0 ? Math.min(currentPageIndex * 20 + projects.length, visibleTotalProjects) : 0;

	return (
		<div>
			<div>
				{showDeleteModal ? (
					<>
						<DeleteProjectModal
							setShowDeleteProjectModal={setShowDeleteModal}
							projectName={toBeDeletedProject.name} //toBeDeletedProject.name
							deleteFunction={() => {
								deleteDuplicateProject(toBeDeletedProject);
							}}
							deleteFunctionCompleted={showDeleteCompleteMessage}
							completedMessage={deleteMessage}
							actionVerb={"Delete"}
							isClearing={false}
							isLoading={isDeleting}
							deleteFailed={deleteFailed}
						/>
					</>
				) : null}
			</div>

			<div className="py-4 md:pr-6 flex flex-col justify-center items-left ">
				<div className="flex flex-col md:flex-row">
					{creatingProject ? (
						<div className="text-black fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black/50 z-50">
							<div className="bg-white flex flex-col items-center justify-center p-4 rounded-lg ">
								<LoadingCircle />
								<p>Creating project...</p>
							</div>
						</div>
					) : null}
					{errorCreatingProject ? (
						<div className="text-black fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black/50 z-50">
							<div className="bg-white flex flex-col items-center justify-center p-4 rounded-lg w-3/5 md:w-2/5 text-center">
								<RiErrorWarningLine size={40} />
								<p>Something went wrong. Please contact IT for assistance or refresh and try again.</p>
								<button className="p-2 mt-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600" onClick={() => window.location.reload()}>
									Refresh
								</button>
							</div>
						</div>
					) : null}
					<div className="md:w-1/5 md:h-100 md:m-2 justify-center md:pt-24 md:mt-6 pt-7">
						{props.isPublicStorage ? null : (
							<>
								<div className="border border-black p-1 rounded-lg md:w-45 md:h-30 md:m-1.5 justify-center pb-7">
									<h1>
										<div className="flex md:flex-row px-1">
											<div className="w-2/5 text-left text-xl">
												<FontAwesomeIcon icon={faFilter} size="sm" /> Filter
											</div>
											<div className="w-3/5 text-right text-md pt-1">
												<span>
													<button className="text-center text-md text-blue-600 hover:text-red-600" onClick={clearAll}>
														Clear All
													</button>
												</span>
											</div>
										</div>
									</h1>
									<div className="p-1 pr-3 mt-3">
										<div className="md:basis-1/4 flex justify-start">
											<p className="display:inline text-left">Contract Type</p>
										</div>
										<div>
											<Select
												styles={{
													control: (baseStyles, state) => ({
														...baseStyles,
														borderColor: state.isFocused ? "blue" : "black",
														"&:hover": {
															cursor: "pointer",
															boxShadow:
																"rgba(0, 0, 0, 0.2) 0px 12px 28px 0px, rgba(0, 0, 0, 0.1) 0px 2px 4px 0px, rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset;",
														},
													}),
												}}
												defaultValue={
													searchParams.get("contract") === "Residential"
														? { label: contractTypes[0].label, value: contractTypes[0].value }
														: searchParams.get("contract") === "Commercial"
															? { label: contractTypes[1].label, value: contractTypes[1].value }
															: null
												}
												options={contractTypes}
												onChange={(contractTypes) =>
													!contractTypes ? setSearchContractType("") : setSearchContractType(contractTypes.value)
												}
												ref={contractInput}
												isClearable={isClearable}
											/>
										</div>
									</div>

									<div className="p-1 pr-3 mt-3">
										<div className="md:basis-1/4 flex justify-start">
											<p className="display:inline text-left">Project Stage</p>
										</div>
										<div>
											<Select
												styles={{
													control: (baseStyles, state) => ({
														...baseStyles,
														borderColor: state.isFocused ? "blue" : "black",
														"&:hover": {
															cursor: "pointer",
															boxShadow:
																"rgba(0, 0, 0, 0.2) 0px 12px 28px 0px, rgba(0, 0, 0, 0.1) 0px 2px 4px 0px, rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset;",
														},
													}),
												}}
												defaultValue={
													searchParams.get("stage") === "Assign"
														? { label: stages[0].label, value: stages[0].value }
														: searchParams.get("stage") === "In-Progress"
															? { label: stages[1].label, value: stages[1].value }
															: searchParams.get("stage") === "Finalize"
																? { label: stages[2].label, value: stages[2].value }
																: searchParams.get("stage") === "Completed"
																	? { label: stages[3].label, value: stages[3].value }
																	: null
												}
												options={stages}
												onChange={(stages) => (!stages ? setSearchProjectStage("") : setSearchProjectStage(stages.value))}
												ref={stageInput}
												isClearable={isClearable}
											/>
										</div>
									</div>

									<div className="p-1 pr-3 mt-3">
										<div className="md:basis-1/4 flex justify-start">
											<p className="display:inline text-left">Odoo Model</p>
										</div>
										<div>
											<Select
												styles={{
													control: (baseStyles, state) => ({
														...baseStyles,
														borderColor: state.isFocused ? "blue" : "black",
														"&:hover": {
															cursor: "pointer",
															boxShadow:
																"rgba(0, 0, 0, 0.2) 0px 12px 28px 0px, rgba(0, 0, 0, 0.1) 0px 2px 4px 0px, rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset;",
														},
													}),
												}}
												value={odooModel}
												options={Object.values(allowedOdooModels)}
												onChange={(models) => (!models ? setOdooModel(defaultModel) : setOdooModel(models))}
												ref={stageInput}
												isClearable={isClearable}
											/>
										</div>
									</div>

									<div className="p-1 pr-3 mt-3">
										<div className="md:basis-1/4 flex justify-start">
											<p className="display:inline text-left">Template Name</p>
										</div>
										<div>
											<Select
												styles={{
													control: (baseStyles, state) => ({
														...baseStyles,
														borderColor: state.isFocused ? "blue" : "black",
														"&:hover": {
															cursor: "pointer",
															boxShadow:
																"rgba(0, 0, 0, 0.2) 0px 12px 28px 0px, rgba(0, 0, 0, 0.1) 0px 2px 4px 0px, rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset;",
														},
													}),
												}}
												value={searchTemplateType}
												options={filteredTemplates}
												onChange={(templateNames) =>
													!templateNames ? setSearchTemplateType("") : setSearchTemplateType(templateNames)
												}
												ref={templateInput}
												isClearable={isClearable}
											/>
										</div>
									</div>
								</div>
							</>
						)}
					</div>

					<div className="py-4 md:pr-6 flex flex-col justify-center items-left md:w-3/5">
						<div id={paginationAnchorId} className="w-full md:w-5/5">
							{props.isPublicStorage ? (
								<p className="text-center text-3xl mb-4">Projects</p>
							) : (
								<p className="text-center text-3xl mb-4">{`${initialOdooModel ? initialOdooModel.label + " Projects" : "Searching..."} `}</p>
							)}
							<form onSubmit={(e) => startSearch(e)} className="my-4 flex w-full justify-center items-center">
								<input
									type="text"
									className="border border-black p-2 w-full rounded-l-lg"
									placeholder="Search for a project..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
								/>
								<button type="submit" className="border rounded-r-lg border-black p-3">
									<BsSearch className="" />
								</button>
							</form>
							{isAllModelSearch ? (
								<div className="mb-4 rounded-lg border border-black bg-gray-100 p-3">
									<p className="text-left text-sm font-semibold">{`Search results: ${visibleTotalProjects}`}</p>
									{modelBreakdownItems.length > 0 ? (
										<div className="mt-3 flex flex-wrap gap-2">
											{modelBreakdownItems.map((modelData) => (
												<button
													type="button"
													key={modelData.value}
													className="rounded-lg border border-black bg-white px-3 py-1 text-sm hover:bg-gray-200"
													onClick={() => handleModelBreakdownClick(modelData.value)}>
													{`${modelData.label} (${modelData.count})`}
												</button>
											))}
										</div>
									) : null}
								</div>
							) : (
								<p className="text-left text-sm mb-3">{`Searched for: ${highlightedSearchTerm || "(blank)"} (${visibleTotalProjects})`}</p>
							)}
							<div>
								<p className="text-2xl bg-gray-300 text-center p-3 border border-black p-4 rounded-t-lg"></p>
								<div className="gap-3 w-full border border-black p-2 hidden md:flex justify-between bg-black text-white md:text-center">
									{/* <p className="md:basis-1/2">Project <button className="" onClick={sortByProject}> <img src={IoArrowUp} className="w-auto h-5" /> </button></p> */}

									{currentModelParam === "helpdesk.ticket" || currentModelParam === "all" ? (
										<div className="md:basis-1/4 flex justify-center">
											<p className="display:inline text-right">{currentModelParam === "all" ? "Odoo ID" : "Ticket #"}</p>
										</div>
									) : null}

									<div className="md:basis-1/4 flex justify-center">
										<p className="display:inline text-right">Project</p>
									</div>

									<p className="md:basis-1/4">Contract Type</p>

									{currentModelParam === "helpdesk.ticket" ? <p className="md:basis-1/4">Status</p> : null}

									<p className="md:basis-1/4">Project Stage</p>
									{/* <p className="md:basis-1/2">Template Name <button onClick={sortByTemplate}> <img src={sortButton} className="w-auto h-5" /> </button></p> */}

									<div className="md:basis-1/4 flex justify-center">
										<p className="display:inline text-right">Template Name</p>
									</div>
									<div className="md:basis-1/4 flex justify-center">
										<p className="display:inline text-right">Last Updated</p>
									</div>
									<div className="flex justify-center invisible">
										<p>delete</p>
									</div>
								</div>
								{projects.length > 0 ? (
									isAllModelSearch ? (
										groupedPaginatedProjects.map((group, groupIndex) => (
											<div key={`${group.model}-${groupIndex}`}>
												<div className="sticky top-0 z-10 border-x border-b border-black bg-blue-100 px-3 py-2 font-semibold">
													{`${group.label} (${projectCounts[group.model] ?? group.items.length})`}
												</div>
												{group.items.map((item, itemIndex) =>
													renderProjectRow(
														item.project,
														item.pageIndex,
														groupIndex === groupedPaginatedProjects.length - 1 && itemIndex === group.items.length - 1,
													),
												)}
											</div>
										))
									) : (
										projects.map((value, index) => renderProjectRow(value, index, index === projects.length - 1))
									)
								) : isLoading ? (
									<div className="flex flex-col items-center justify-center w-full my-2">
										<LoadingCircle />
										<p>Loading projects</p>
									</div>
								) : (
									<p className="text-center text-xl m-4">0 Results found</p>
								)}
							</div>

							<div className="text-center">
								<span className="text-sm text-gray-700">
									Showing
									<span className="font-semibold text-gray-900"> {visibleStartIndex}</span> to
									<span className="font-semibold text-gray-900"> {visibleEndIndex}</span> of
									<span className="font-semibold text-gray-900"> {visibleTotalProjects}</span> Entries
								</span>

								<nav aria-label="Page navigation example">
									<ul className="inline-flex items-center -space-x-px h-16">
										{PaginationNav1Presentation(pageCount, searchQuery, currentPageIndex, handlePageChange, paginationAnchorId)}
									</ul>
								</nav>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SearchProject;
